import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, call, upload } from "../api/client";
import { useSystems, useUsers } from "../api/queries";
import { ConfirmDialog } from "../components/Dialog";
import { ErrorNote } from "../components/ErrorNote";
import { useCurrentUser } from "../pages/AuthPages";
import { dateTime } from "../text";
import type { CampaignHead, CampaignListItem, WorldView } from "./types";

/** Neutral role names; inside a campaign the terms of its module and its own overrides apply. */
const roleNames = { gameMaster: "Spielleitung", player: "Spieler" } as const;

export const campaignListKey = ["campaigns"] as const;

/** Largest import the server takes; checked before uploading, since a larger request ends without a German message. */
const maxImportBytes = 64 * 1024 * 1024;

export function CampaignListPage() {
  const campaigns = useQuery({ queryKey: campaignListKey, queryFn: () => call<CampaignListItem[]>(api.GET("/api/campaigns")) });
  const navigate = useNavigate();
  const client = useQueryClient();
  const user = useCurrentUser();
  const admin = user.role === "admin";
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  const onImport = async (file: File) => {
    setError(null);
    if (file.size > maxImportBytes) {
      setError(new Error(`„${file.name}“ ist ${Math.ceil(file.size / (1024 * 1024))} MB groß. Ein Kampagnenarchiv darf höchstens 64 MB groß sein.`));
      return;
    }
    setBusy(true);
    try {
      const head = await upload<CampaignHead>("/api/campaigns/import", file);
      await client.invalidateQueries({ queryKey: campaignListKey });
      navigate(`/k/${head.id}`);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <section>
      <header className="page-header">
        <h1>Kampagnen</h1>
        <div className="actions">
          <Link to="/kampagnen/neu" className="button primary">
            Neue Kampagne
          </Link>
          <button type="button" className="button secondary" disabled={busy} onClick={() => input.current?.click()}>
            Importieren
          </button>
          <input
            ref={input}
            type="file"
            accept=".zip,application/zip"
            hidden
            aria-label="Kampagnenarchiv"
            onChange={(e) => e.target.files?.[0] && void onImport(e.target.files[0])}
          />
        </div>
      </header>
      <ErrorNote error={error} />
      {campaigns.isLoading && <p className="muted">Lade …</p>}
      {campaigns.data?.length === 0 && <p className="muted">Noch keine Kampagnen. Lege eine neue an oder importiere ein Archiv.</p>}
      <ul className="character-list" aria-label="Kampagnen">
        {campaigns.data?.map((c) => (
          <li key={c.id}>
            {c.role ? (
              <Link to={`/k/${c.id}`} className="card character-card">
                <CampaignCard campaign={c} />
              </Link>
            ) : (
              <div className="card character-card">
                <CampaignCard campaign={c} />
                <span className="muted small">Ohne Mitgliedschaft: Inhalte sehen nur Mitglieder.</span>
              </div>
            )}
            {admin && <AdminActions campaign={c} selfId={user.id} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

function CampaignCard({ campaign }: { campaign: CampaignListItem }) {
  return (
    <>
      <strong>{campaign.name}</strong>
      <span className="muted">{campaign.systemName.de}</span>
      {campaign.role && <span className="badge">{roleNames[campaign.role]}</span>}
      {campaign.gameMasters.length > 0 && <span className="muted small">{campaign.gameMasters.join(", ")}</span>}
      <span className="muted small">{dateTime(campaign.updatedAt)}</span>
    </>
  );
}

/**
 * What the admin may do with any campaign without seeing its contents (F7): delete it, or give it a game master, for example when
 * the last one left. Not the admin itself; the game masters can add the admin as a member.
 */
function AdminActions({ campaign, selfId }: { campaign: CampaignListItem; selfId: string }) {
  const client = useQueryClient();
  const users = useUsers();
  const [userId, setUserId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  const candidates = (users.data ?? []).filter((u) => u.id !== selfId);
  return (
    <div className="stack admin-actions" aria-label={`Verwaltung von ${campaign.name}`}>
      <form
        className="inline-form two"
        onSubmit={(e) => {
          e.preventDefault();
          if (!userId) return;
          void run(async () => {
            await call(api.POST("/api/campaigns/{id}/gamemasters", { params: { path: { id: campaign.id } }, body: { userId } }));
            setUserId("");
            await client.invalidateQueries({ queryKey: campaignListKey });
          });
        }}
      >
        <label className="field">
          <span>Spielleitung setzen</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">–</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="button secondary" disabled={!userId || busy}>
          Setzen
        </button>
      </form>
      <div className="actions">
        <button type="button" className="button danger" disabled={busy} onClick={() => setDeleting(true)}>
          Kampagne löschen
        </button>
      </div>
      <ErrorNote error={error} />
      {deleting && (
        <ConfirmDialog
          title="Kampagne löschen"
          action="Endgültig löschen"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await call(api.DELETE("/api/campaigns/{id}", { params: { path: { id: campaign.id } } }));
            await client.invalidateQueries({ queryKey: campaignListKey });
          }}
        >
          <p>„{campaign.name}“ wird mit allen Einträgen gelöscht. Das lässt sich nicht rückgängig machen. Die Fakten der Welt bleiben.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

const freeSystem = "";

export function NewCampaignPage() {
  const systems = useSystems();
  const worlds = useQuery({ queryKey: ["worlds"], queryFn: () => call<WorldView[]>(api.GET("/api/worlds")) });
  const navigate = useNavigate();
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [chosen, setModuleId] = useState<string | null>(null);
  const [freeText, setFreeText] = useState("");
  const [worldId, setWorldId] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  // Until the user picks, the first module is the default; without modules the free text.
  const moduleId = chosen ?? systems.data?.[0]?.id ?? freeSystem;

  const system = moduleId === freeSystem ? freeText.trim() : moduleId;
  const matching = (worlds.data ?? []).filter((w) => w.system.localeCompare(system, "de", { sensitivity: "accent" }) === 0);

  // Without a request id on the server, a second click while the first is under way would create a second campaign.
  const sending = useRef(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending.current) return;
    setError(null);
    sending.current = true;
    setBusy(true);
    try {
      if (!name.trim()) throw new Error("Die Kampagne braucht einen Namen.");
      if (!system) throw new Error("Bitte ein System wählen oder angeben.");
      const head = await call<CampaignHead>(api.POST("/api/campaigns", { body: { name, system, worldId: worldId || null } }));
      await client.invalidateQueries({ queryKey: campaignListKey });
      navigate(`/k/${head.id}`);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="stack">
      <h1>Neue Kampagne</h1>
      <form className="card stack" onSubmit={(e) => void submit(e)} noValidate>
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>System</span>
          <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
            {systems.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name.de}
              </option>
            ))}
            <option value={freeSystem}>Anderes System (freie Angabe)</option>
          </select>
        </label>
        {moduleId === freeSystem && (
          <label className="field">
            <span>Name des Systems</span>
            <input value={freeText} onChange={(e) => setFreeText(e.target.value)} placeholder="etwa ein System ohne eigenes Modul" />
          </label>
        )}
        <label className="field">
          <span>Welt</span>
          <select value={worldId} onChange={(e) => setWorldId(e.target.value)}>
            <option value="">{matching.length > 0 ? "Bisherige Welt des Systems" : "Neue Welt für das System"}</option>
            {matching.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
        <p className="muted small">Die Welt hält Fakten, die mehrere Kampagnen desselben Systems teilen.</p>
        <ErrorNote error={error} />
        <button type="submit" className="button primary" disabled={busy}>
          Anlegen
        </button>
      </form>
    </section>
  );
}
